import React, {useEffect, useRef, useState} from "react";
import locales_es from "../../../locales/es";
import APIService from "../../../modules/apiService";
import moment from "moment-timezone";
import {formatNumber} from "../../../utils/formatters";
import {useBillingContext} from "..";
import "./createInvoiceModal.css";
import Spinner from "../../../components/spinner";
import {HREF_PAGE_HELP, PENDING_BILLING_TYPE} from "../../../models/constants";
import {useHistory} from "react-router-dom";

const api = new APIService();

const STATUS = {
  IDLE: "idle",
  SAVING: "saving",
  INVOICED: "invoiced",
  MOVING_TO_NEXT: "movingToNext",
};

export default function CreateInvoiceModal({visible, onClose, items, onInvoiced, onComplete, onSkip}) {
  const history = useHistory();
  const {
    showErrorModal,
    billingSettings: {points_of_sale: pointsOfSale},
  } = useBillingContext();
  const [status, setStatus] = useState(STATUS.IDLE);
  const [currentItem, setCurrentItem] = useState(items[0] ?? null);
  const [form, setForm] = useState({
    invoice_date: "",
    point_of_sale: "",
    patient_name: "",
    patient_identification_number: "",
    detail: "",
    amount_total: "",
  });
  const [medicalInsurances, setMedicalInsurances] = useState(null);
//  const [invoiceTemplates] = useState([{name: 'OSDE Todos los planes', description: 'El paciente...'}]); // null para loading, [{name: 'OSDE Todos los planes', description: 'El paciente...'}] para cuando hay plantillas
  const [errors, setErrors] = useState({});
  const detailRef = useRef();
  const identificationNumberRef = useRef();
  const [initialItemsCount] = useState(items.length);

  const isSaving = status === STATUS.SAVING;
  const isInvoiced = status === STATUS.INVOICED;
  const isMovingToNextItem = status === STATUS.MOVING_TO_NEXT;

  const handleFormChange = (e) => {
    setForm({...form, [e.target.name]: e.target.value});
  };

  const moveToNextItem = () => {
    setStatus(STATUS.MOVING_TO_NEXT);

    if (items.length > 1) {
      setTimeout(() => {
        setStatus(STATUS.IDLE);
        setCurrentItem(items[1]);
      }, 300);
    } else {
      onComplete();
      onClose();
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setStatus(STATUS.SAVING);
    setErrors({});

    try {
      const response = await api.postInvoices({
        ...form,
        patient_id: currentItem.patient?.id,
        source_type: currentItem.type,
        source_id: currentItem.id,
      });

      const data = await response.json();

      if (response.ok) {
        setStatus(STATUS.INVOICED);
        onInvoiced(currentItem);

        setTimeout(() => {
          moveToNextItem();
        }, 500);
      } else if (response.status === 422) {
        setErrors(data?.errors ?? {});
        setStatus(STATUS.IDLE);
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      showErrorModal(err.message);
      setStatus(STATUS.IDLE);
    }
  };

  const handleSkip = () => {
    onSkip(currentItem);
    moveToNextItem();
  };

  const setFieldInDescription = (fieldName) => {
    const {patient} = currentItem;

    if (patient && form[fieldName]) {
      setForm({
        ...form,
        detail: `${form.detail} ${form[fieldName]}`,
      });
    }
  }

  const addStringToDescription = (string) => {
    const {patient} = currentItem;

    if (patient) {
      setForm({
        ...form,
        detail: `${form.detail} ${string}`,
      });
    }
  }

  const onclickSeeTutorial = (e) => {
    e && e.preventDefault();
    history.push(HREF_PAGE_HELP + "?guide_category_id=8&article_id=10");
  }

  useEffect(() => {
    if (currentItem) {
      setForm({
        invoice_date: moment().format("YYYY-MM-DD"),
        point_of_sale: pointsOfSale && pointsOfSale.length ? pointsOfSale[0] : "",
        patient_name: currentItem.patient?.fullname || "",
        patient_identification_number: currentItem.patient?.identification || "",
        detail: "",
        amount_total: currentItem.price || "0",
      });
      if (currentItem.patient?.id) {
        api.getMedicalInsuranceByPatient(currentItem.patient.id)
          .then((res) => {
            const medicalInsurances = res.data.map(insurance => {
              return ({
                id: insurance.id,
                name: `${insurance.medical_insurance.name} - ${insurance.plan} - N°${insurance.number}`
              })
            });
            setMedicalInsurances(medicalInsurances);
          })
          .catch((error) => {
            showErrorModal(error.message);
          });
      }
    }
  }, [currentItem, pointsOfSale]);

  useEffect(() => {
    if (visible && currentItem) {
      if (currentItem.patient?.identification) {
        detailRef.current?.focus();
      } else {
        identificationNumberRef.current?.focus();
      }
    }
  }, [visible, currentItem]);

  return !visible || !currentItem ? null : (
    <div className="invoice-modal">
      <div className="invoice-modal-content">
        <div className="invoice-modal-header">
          {items
            .filter((item) => item.id !== currentItem?.id)
            .map((item, index) => (
              <div
                key={item.id}
                className="invoice-modal-header-item rounded-lg bg-light"
                data-index={index}
                style={{
                  zIndex: items.length - index,
                  transform: `scale(${0.975 - 0.025 * index})`,
                }}
              ></div>
            ))}
        </div>

        <div className="invoice-modal-body p-4 rounded-lg bg-white">
          <form onSubmit={handleSave}>
            <div className={`${isMovingToNextItem ? "fade-out" : "fade-in"}`}>
              <h3 className="text-center mb-4">
                {PENDING_BILLING_TYPE[currentItem.type] || currentItem.type} {formatNumber(currentItem.id, 0)} - {currentItem.patient?.fullname || "Sin nombre"}
              </h3>
              <div className="form-group row">
                <label htmlFor="invoice_date" className="col-sm-2 col-form-label col-form-label-sm">
                  Fecha *
                </label>
                <div className="col-sm-10">
                  <input type="date" className={`form-control ${errors.invoice_date && "is-invalid"}`} id="invoice_date"
                         name="invoice_date" value={form.invoice_date} onChange={handleFormChange} disabled={isSaving}
                         required/>
                  {errors.invoice_date && <div className="invalid-feedback">{errors.invoice_date[0]}</div>}
                </div>
              </div>
              <div className="form-group row">
                <label htmlFor="point_of_sale" className="col-sm-2 col-form-label col-form-label-sm">
                  Punto de venta *
                </label>
                <div className="col-sm-10">
                  <select className={`form-control ${errors.point_of_sale && "is-invalid"}`} id="point_of_sale"
                          name="point_of_sale" value={form.point_of_sale} onChange={handleFormChange}
                          disabled={isSaving} required>
                    <option value="">Seleccionar...</option>
                    {pointsOfSale && pointsOfSale.length ? pointsOfSale.map((pointOfSale, index) => (
                      <option key={index} value={pointOfSale}>
                        {pointOfSale}
                      </option>
                    )) : <option value="">No hay puntos de venta disponibles. Debe crear uno desde AFIP (ARCA)</option>}
                  </select>
                  {errors.point_of_sale && <div className="invalid-feedback">{errors.point_of_sale[0]}</div>}
                </div>
              </div>
              <div className="form-group row">
                    Debes dar de alta un punto de venta compatible - <a className="" href="#" onClick={onclickSeeTutorial}>Tutorial en 3 simples pasos.</a>
                </div>
              <div className="form-group row">
                <label htmlFor="patient_fullname" className="col-sm-2 col-form-label col-form-label-sm">
                  Paciente
                </label>
                <div className="col-sm-10">
                  <input className={`form-control ${errors.patient_name && "is-invalid"}`} type="text" id="patient_name"
                         name="patient_name" value={form.patient_name} onChange={handleFormChange} disabled={isSaving}/>
                  {errors.patient_name && <div className="invalid-feedback">{errors.patient_name[0]}</div>}
                </div>
              </div>
              <div className="form-group row">
                <label htmlFor="patient_identification_number" className="col-sm-2 col-form-label col-form-label-sm">
                  Documento *
                </label>
                <div className="col-sm-10">
                  <input ref={identificationNumberRef}
                         className={`form-control ${errors.patient_identification_number && "is-invalid"}`} type="text"
                         id="patient_identification_number" name="patient_identification_number"
                         value={form.patient_identification_number} onChange={handleFormChange} disabled={isSaving}
                         required/>
                  {errors.patient_identification_number &&
                    <div className="invalid-feedback">{errors.patient_identification_number[0]}</div>}
                </div>
              </div>
              <div className="form-group row">
                <label htmlFor="detail" className="col-sm-2 col-form-label col-form-label-sm">
                  Detalle *
                </label>
                <div className="col-sm-10">
                  <textarea ref={detailRef} className={`form-control ${errors.detail && "is-invalid"}`} type="text"
                            id="detail" name="detail" value={form.detail} rows={5} onChange={handleFormChange}
                            disabled={isSaving} required/>
                  {errors.detail && <div className="invalid-feedback">{errors.detail[0]}</div>}
                </div>
                <div className="col text-right">
                  Autocompletar
                  <button onClick={() => setFieldInDescription('patient_name')} type="button"
                          className="btn btn-secondary btn-elevate btn-icon m-1">
                    <i className="la la-user"></i>
                  </button>
                  <button onClick={() => setFieldInDescription('patient_identification_number')} type="button"
                          className="btn btn-secondary btn-elevate btn-icon m-1">DNI
                  </button>
                  {medicalInsurances === null ? <Spinner className="d-inline-block w-auto"/> :
                    <>
                      <a className="btn btn-secondary dropdown-toggle" href="#" role="button" id="dropdownMenuLink"
                         data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        Coberturas del Paciente
                      </a>
                      <div className="dropdown-menu" aria-labelledby="dropdownMenuLink" x-placement="bottom-start">
                        {medicalInsurances.length ? medicalInsurances.map((insurance) => (
                          <option onClick={() => addStringToDescription(insurance.name)}
                                  className="dropdown-item cursor-pointer">{insurance.name}</option>
                        )) : <option onClick={() => alert('Este paciente no tiene coberturas médicos.')}
                                     className="dropdown-item cursor-pointer">Sin coberturas</option>}
                      </div>
                    </>
                  }
                  <br/>
                  {/*
                  Plantillas
                  {invoiceTemplates === null ? <Spinner className="d-inline-block w-auto"/> :
                    invoiceTemplates && invoiceTemplates.length ?
                      <div className="btn-group m-1">
                        <button type="button" className="btn btn-secondary dropdown-toggle" data-toggle="dropdown"
                                aria-haspopup="true" aria-expanded="false">
                          Ver plantillas
                        </button>
                        <div className="dropdown-menu" x-placement="bottom-start">
                          {invoiceTemplates.map((template) => (
                            <a className="dropdown-item" href="#">{template.name} | <a className="text-info pl-1 pr-1">Cargar</a> | <a className="text-danger pl-1">Eliminar</a></a>
                          ))}
                          <div className="dropdown-divider"></div>
                          <a className="dropdown-item" href="#">Guardar esta descripción</a>
                        </div>
                      </div>
                      :
                      <button onClick={() => setFieldInDescription('patient_name')} type="button"
                          className="btn btn-secondary btn-elevate m-1">
                        <i className="la la-save"></i>  Guardar esta descripción como plantilla
                      </button>
                  }*/}
                </div>
              </div>
              <div className="form-group row">
                <label htmlFor="amount_total" className="col-sm-2 col-form-label col-form-label-sm">
                  Total *
                </label>
                <div className="col-sm-10">
                  <input className={`form-control ${errors.amount_total && "is-invalid"}`} type="number" step="0.01"
                         id="amount_total" name="amount_total" value={form.amount_total} onChange={handleFormChange}
                         disabled={isSaving} required/>
                  {errors.amount_total && <div className="invalid-feedback">{errors.amount_total[0]}</div>}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <div
                className="text-muted">{`${initialItemsCount - items.length + 1} de ${initialItemsCount} items facturados`}</div>
              {isInvoiced ? (
                <div
                  className="text-success"
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                  }}
                >
                  ¡Facturado!
                </div>
              ) : (
                <div>
                  <button type="button" className="btn btn-warning mr-2" onClick={handleSkip} disabled={isSaving}>
                    Omitir
                  </button>
                  <button type="submit" className="btn btn-success mr-2" disabled={isSaving}>
                    {isSaving ? (
                      <div className="d-flex align-items-center" style={{gap: "5px"}}>
                        <div className="spinner-border spinner-border-sm" role="status"/>
                        Facturando...
                      </div>
                    ) : (
                      "Facturar"
                    )}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
                    {locales_es.cancel}
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
